import { useLocation } from 'react-router-dom'
import ReportCenterTabs from './ReportCenterTabs'
import { REPORTS_LIST_PATH, QUEUE_PATH } from '@config/reportRoutes'
import { useDashboardsTab } from '@hooks/useUserSettings'
import useMyReports from '@hooks/useMyReports'

// Same derivation the page used (Reports.jsx:15) — featureFlags.js does not
// export this one.
const REPORT_JOBS = import.meta.env.VITE_REPORT_JOBS === 'true'

/*
 * The catalog's bare PATHNAME.
 *
 * `REPORTS_LIST_PATH` is NOT a pathname — it is `'/reports?mode=reports'`, a path
 * with a query string baked in (`reportRoutes.js:16`). Using it as one produced
 * two bugs at once: `pathname === REPORTS_LIST_PATH` was false for every URL that
 * can exist, so the Dashboards tab could never be active; and
 * `` `${REPORTS_LIST_PATH}?mode=dashboards` `` emitted
 * `/reports?mode=reports?mode=dashboards`, which React Router parses as
 * mode="reports?mode=reports" — so clicking "Reports" in the header landed the
 * user on Dashboards.
 *
 * Compare against this for route tests; use REPORTS_LIST_PATH verbatim for links
 * that want the catalog.
 */
const CATALOG_PATHNAME = '/reports'

/**
 * Report Center's top-level nav — Dashboards | Reports | Queue — for the TOP BAR.
 *
 * The Figma draws this group inside the top bar (Row B, `110:9246`), not on the
 * page. It used to render on the page, from THREE separate call sites that each
 * built the tab list themselves: `Reports.jsx`, `ReportQueue.jsx` and
 * `ReportSchedules.jsx`. They had drifted — Reports used in-page `?mode=`
 * toggles while the other two used route links, the Queue badge was computed two
 * different ways, and Schedules showed no badge at all. Three implementations of
 * one nav is the same shape of defect as the AI summary (CC-53111).
 *
 * So the group is derived ONCE, here, from the route.
 *
 * ## Why the mode toggles become links
 *
 * On `/reports`, Dashboards and Reports are the same route distinguished by
 * `?mode=` (`Reports.jsx:123`), which the page reads straight out of the URL. The
 * old in-page `onSelect` buttons therefore had a URL equivalent all along, and
 * using it is strictly better: each view becomes linkable, bookmarkable and
 * back-button-correct. The header has no page state and could not have called
 * `setMode` anyway.
 *
 * ## The two gates, both preserved
 *
 * `useDashboardsTab()` is the two-layer gate spec 038 requires — the build flag
 * AND the per-user opt-in. `ReportQueue` and `ReportSchedules` both carry a
 * comment that checking only the flag gave users a Dashboards tab landing on the
 * reports catalog; deriving it here keeps that fix in one place.
 *
 * The Queue count is read from `useMyReports`, the same hook the page used
 * (`Reports.jsx:128`) and the one `NotificationCenter` already subscribes to, so
 * this adds no new poll — react-query dedupes the key. It is only enabled when
 * `REPORT_JOBS` is on; with jobs off the tab still renders (it is a peer
 * destination and must stay reachable) but carries no badge, rather than
 * asserting a "0" we have not measured.
 */
/*
 * Split in two ON PURPOSE, and this is the load-bearing part.
 *
 * The header renders on all 36 routes. If the hooks below lived in the outer
 * component they would run on every one of them — `useDashboardsTab()` fires a
 * `user-settings` query and `useMyReports()` polls the jobs list, so mounting
 * /matters or /contacts would start two Report Center reads that page has no use
 * for. Measured while building this: the query fired on /dashboard, /matters and
 * /contacts before the split.
 *
 * Hooks cannot be called conditionally, so the route test has to happen in a
 * PARENT that renders nothing outside Report Center. Then the hooks genuinely do
 * not exist there — not merely disabled.
 */
const ReportCenterNav = () => {
  const { pathname, search } = useLocation()
  // A nav for one product area has no business claiming a current tab on /matters.
  if (!pathname.startsWith('/reports')) return null
  return <Nav pathname={pathname} search={search} />
}

const Nav = ({ pathname, search }) => {
  const dashboardsEnabled = useDashboardsTab()
  const { runningCount, readyCount } = useMyReports({ enabled: REPORT_JOBS })

  const params = new URLSearchParams(search)
  const onQueue = pathname.startsWith(QUEUE_PATH) || pathname.startsWith('/reports/schedules')
  // Mirrors Reports.jsx:123 exactly: dashboards is the default landing view, but
  // an explicit ?mode=reports or any active search forces the catalog. Diverging
  // here would light the wrong tab on the page the user is looking at.
  const hasQuery = (params.get('q') || '').trim().length > 0
  const onDashboards =
    !onQueue && dashboardsEnabled && params.get('mode') !== 'reports' && !hasQuery && pathname === CATALOG_PATHNAME

  const activeKey = onQueue ? 'queue' : onDashboards ? 'dashboards' : 'reports'
  const queueCount = REPORT_JOBS ? runningCount + readyCount : undefined

  return (
    <ReportCenterTabs
      variant="header"
      activeKey={activeKey}
      tabs={[
        ...(dashboardsEnabled
          ? [{ key: 'dashboards', label: 'Dashboards', to: `${CATALOG_PATHNAME}?mode=dashboards` }]
          : []),
        // REPORTS_LIST_PATH already carries ?mode=reports — use it verbatim.
        { key: 'reports', label: 'Reports', to: REPORTS_LIST_PATH },
        { key: 'queue', label: 'Queue', to: QUEUE_PATH, count: queueCount },
      ]}
    />
  )
}

export default ReportCenterNav
